import React, { useEffect, useState } from 'react';
import { StatusBar, Platform, Linking, Alert, Text, TextInput, TextStyle, View, StyleSheet } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import * as NavigationBar from 'expo-navigation-bar';
import { useFonts } from 'expo-font';
import {
  Poppins_300Light,
  Poppins_400Regular,
  Poppins_500Medium,
  Poppins_600SemiBold,
  Poppins_700Bold,
} from '@expo-google-fonts/poppins';
import { AuthProvider } from './src/context/AuthContext';
import { WorkProvider } from './src/context/WorkContext';
import { ChatProvider } from './src/context/ChatContext';
import { NotificationProvider } from './src/context/NotificationContext';
import { TutorialProvider } from './src/context/TutorialContext';
import { ThemeProvider, useTheme } from './src/theme/ThemeContext';
import RootNavigator from './src/navigation/RootNavigator';
import { initializeMockLedgerData } from './src/data/mockLedgerData';
import BrandSplash from './src/components/common/BrandSplash';
import TutorialOverlay from './src/components/tutorial/TutorialOverlay';
import { onForegroundNotification, onBackgroundNotificationOpen, getInitialNotification } from './src/services/notifications.native';
import { getPost } from './src/lib/api/feed';
import { storage } from './src/utils/storage';

// Firebase is initialized in index.ts before this file loads

type CustomComponentProps = {
  style?: TextStyle;
  [key: string]: any;
};

let didApplyGlobalTextStyles = false;

const applyGlobalTextStyles = (customProps: CustomComponentProps) => {
  if (didApplyGlobalTextStyles) {
    return;
  }

  [Text, TextInput].forEach((Component: any) => {
    const ComponentRender = Component.render;
    const initialDefaultProps = Component.defaultProps;

    Component.defaultProps = {
      ...initialDefaultProps,
      ...customProps,
    };

    if (customProps.style) {
      Component.render = function render(props: any, ref: any) {
        const mergedProps = {
          ...props,
          style: [customProps.style, props?.style].filter(Boolean),
        };

        return ComponentRender.call(this, mergedProps, ref);
      };
    }
  });

  didApplyGlobalTextStyles = true;
};

const AppContent = () => {
  const { isDark, theme } = useTheme();
  const navigationRef = React.useRef<any>(null);

  // Handle foreground notifications (native only)
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    console.log('🔔 Setting up foreground notification handler...');
    const unsubscribe = onForegroundNotification((remoteMessage) => {
      console.log('📬 Foreground notification received:', remoteMessage);

      if (remoteMessage.notification) {
        const { title, body } = remoteMessage.notification;
        console.log('Notification Title:', title);
        console.log('Notification Body:', body);

        // Show alert for foreground notifications
        Alert.alert(
          title || 'New Notification',
          body || '',
          [{ text: 'OK' }]
        );
      }
    });

    return unsubscribe;
  }, []);

  // Handle notification opened from background (native only)
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    console.log('🔔 Setting up background notification open handler...');
    const unsubscribe = onBackgroundNotificationOpen((remoteMessage) => {
      console.log('📬 Notification opened from background:', remoteMessage);

      // Handle navigation based on notification data
      if (remoteMessage.data) {
        handleNotificationNavigation(remoteMessage.data);
      }
    });

    return unsubscribe;
  }, []);

  // Check for initial notification (app opened from quit state) - native only
  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    getInitialNotification().then((remoteMessage) => {
      if (remoteMessage) {
        console.log('📬 App opened from notification:', remoteMessage);

        // Handle navigation based on notification data
        if (remoteMessage.data) {
          setTimeout(() => {
            handleNotificationNavigation(remoteMessage.data);
          }, 1000);
        }
      }
    });
  }, []);

  // Helper function to handle notification navigation
  const handleNotificationNavigation = (data: any) => {
    const { referenceType, referenceId, type, metadata } = data;

    console.log('🔔 Handling notification navigation:', { referenceType, referenceId, type });

    if (referenceType === 'club' && referenceId) {
      // Check if this is a meetup notification
      const isMeetupNotification = 
        type === 'MEETUP_REQUEST' || 
        type === 'MEETUP_ACCEPTED' || 
        type === 'MEETUP_REJECTED';

      // Check if this is a referral intro notification
      const isReferralNotification =
        (metadata?.isReferralIntro && metadata?.clubId) ||
        (type === 'POST_COMMENTED' && data.title?.includes('Referral')) ||
        (type === 'QUOTATION_ACCEPTED' && data.title?.includes('Intro')) ||
        (type === 'QUOTATION_REJECTED' && data.title?.includes('Intro'));

      if (isMeetupNotification) {
        console.log('📍 Navigating to club meetups tab:', referenceId);
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Clubs',
          params: {
            screen: 'ClubDetail',
            params: { 
              clubId: referenceId,
              initialTab: 'meetups',
            },
          },
        } as never);
      } else if (isReferralNotification) {
        const clubId = metadata?.clubId;
        console.log('📍 Navigating to referral request:', { requestId: referenceId, clubId });

        if (clubId) {
          navigationRef.current?.navigate('MainTabs' as never, {
            screen: 'Clubs',
            params: {
              screen: 'ReferralRequestDetail',
              params: {
                requestId: referenceId,
                clubId: clubId,
              },
            },
          } as never);
        }
      } else {
        console.log('📍 Navigating to club:', referenceId);
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Clubs',
          params: {
            screen: 'ClubDetail',
            params: { clubId: referenceId },
          },
        } as never);
      }
    } else if (referenceType === 'ledger' && referenceId) {
      console.log('📍 Navigating to ledger');
      navigationRef.current?.navigate('MainTabs' as never, {
        screen: 'Ledger',
      } as never);
    } else if (referenceType === 'connection' && referenceId) {
      console.log('📍 Navigating to connection');

      if (type === 'CONNECTION_REQUEST_RECEIVED') {
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Network',
          params: { initialView: 'requests' },
        } as never);
      } else if (type === 'CONNECTION_REQUEST_ACCEPTED') {
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Network',
          params: { initialView: 'my-network' },
        } as never);
      } else {
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Network',
        } as never);
      }
    } else if (referenceType === 'quote' && referenceId) {
      const jobId = metadata?.jobId;
      console.log('📍 Navigating to contract details for job:', jobId);

      if (jobId) {
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Work',
          params: {
            screen: 'ContractDetail',
            params: { jobId },
          },
        } as never);
      } else {
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Work',
        } as never);
      }
    } else if (referenceType === 'post' && referenceId) {
      console.log('📍 Navigating to post:', referenceId);

      // Fetch the post and navigate
      (async () => {
        try {
          const token = await storage.getToken();
          const post = await getPost(token || undefined, referenceId);
          navigationRef.current?.navigate('PostDetail' as never, { post } as never);
        } catch (error) {
          console.error('Failed to fetch post:', error);
        }
      })();
    } else if (referenceType === 'chat' && referenceId) {
      console.log('📍 Navigating to chat conversation:', referenceId);

      const conversationId = data.conversationId || referenceId;
      const senderId = data.senderId;
      const senderName = data.senderName;

      // For chat notifications, senderId is the person who sent the message
      // We want to open the chat with that person
      if (senderId && senderName && conversationId) {
        console.log('📍 Opening personal chat with:', { senderId, senderName, conversationId });

        // Navigate directly to PersonalChat
        setTimeout(() => {
          navigationRef.current?.navigate('PersonalChat' as never, {
            userId: senderId,
            userName: senderName,
            conversationId: conversationId,
          } as never);
        }, 500);
      } else {
        // Fallback: just open Chats tab
        navigationRef.current?.navigate('MainTabs' as never, {
          screen: 'Chats',
        } as never);
      }
    }
  };

  // Handle deep links
  useEffect(() => {
    const handleDeepLink = (event: { url: string }) => {
      const url = event.url;
      console.log('🔗 Deep link received:', url);

      // Parse deep link URL
      // Format: https://lnbapp.ln.ink/profile/{userId} or https://lnbapp.ln.ink/post/{postId}
      // Also supports hash format: https://lnbapp.ln.ink/#/profile/{userId}

      // Check for club link
      const clubMatch = url.match(/\/club\/([^\/\?]+)/);
      if (clubMatch && clubMatch[1]) {
        const clubId = clubMatch[1];
        console.log('📱 Navigating to club:', clubId);

        // Navigate to club detail after a short delay to ensure navigation is ready
        setTimeout(() => {
          navigationRef.current?.navigate('MainTabs' as never, {
            screen: 'Clubs',
            params: {
              screen: 'ClubDetail',
              params: { clubId },
            },
          } as never);
        }, 500);
        return;
      }

      // Check for profile link
      const profileMatch = url.match(/\/profile\/([^\/\?]+)/);
      if (profileMatch && profileMatch[1]) {
        const userId = profileMatch[1];
        console.log('📱 Navigating to profile:', userId);

        // Navigate to profile after a short delay to ensure navigation is ready
        setTimeout(() => {
          navigationRef.current?.navigate('Profile', { userId });
        }, 500);
        return;
      }

      // Check for post link
      const postMatch = url.match(/\/post\/([^\/\?]+)/);
      if (postMatch && postMatch[1]) {
        const postId = postMatch[1];
        console.log('📱 Navigating to post:', postId);

        // Fetch the post and navigate to PostDetail
        setTimeout(async () => {
          try {
            const token = await storage.getToken();
            const post = await getPost(token || undefined, postId);

            // Navigate to PostDetail at root level (like Profile)
            navigationRef.current?.navigate('PostDetail', { post });
          } catch (error) {
            console.error('Failed to fetch post:', error);
            Alert.alert('Error', 'Could not load the post. It may have been deleted.');
          }
        }, 500);
        return;
      }

      // Check for contract link
      const contractMatch = url.match(/\/contract\/([^\/\?]+)/);
      if (contractMatch && contractMatch[1]) {
        const jobId = contractMatch[1];
        console.log('📱 Navigating to contract:', jobId);

        // Navigate to contract detail after a short delay to ensure navigation is ready
        setTimeout(() => {
          navigationRef.current?.navigate('MainTabs' as never, {
            screen: 'Work',
            params: {
              screen: 'ContractDetail',
              params: { jobId },
            },
          } as never);
        }, 500);
        return;
      }

      // Check for supply link
      const supplyMatch = url.match(/\/supply\/([^\/\?]+)/);
      if (supplyMatch && supplyMatch[1]) {
        const jobId = supplyMatch[1];
        console.log('📱 Navigating to supply:', jobId);

        // Navigate to supply detail after a short delay to ensure navigation is ready
        setTimeout(() => {
          navigationRef.current?.navigate('MainTabs' as never, {
            screen: 'Work',
            params: {
              screen: 'SupplyDetail',
              params: { jobId },
            },
          } as never);
        }, 500);
        return;
      }

      // Check for product link
      const productMatch = url.match(/\/product\/([^\/\?]+)/);
      if (productMatch && productMatch[1]) {
        const productId = productMatch[1];
        console.log('📱 Navigating to product:', productId);

        // Navigate to product detail after a short delay to ensure navigation is ready
        setTimeout(() => {
          navigationRef.current?.navigate('MainTabs' as never, {
            screen: 'Work',
            params: {
              screen: 'ProductDetail',
              params: { productId },
            },
          } as never);
        }, 500);
        return;
      }
    };

    // Handle initial URL (app opened from link)
    Linking.getInitialURL().then((url) => {
      if (url) {
        handleDeepLink({ url });
      } else if (Platform.OS === 'web' && typeof window !== 'undefined') {
        // On web, check for query parameters first (from function redirect)
        const searchParams = new URLSearchParams(window.location.search);
        const openPost = searchParams.get('openPost');
        const openProfile = searchParams.get('openProfile');
        const openClub = searchParams.get('openClub');
        const openContract = searchParams.get('openContract');
        const openSupply = searchParams.get('openSupply');
        const openProduct = searchParams.get('openProduct');

        if (openPost) {
          console.log('🌐 Web redirect to post:', openPost);
          handleDeepLink({ url: `${window.location.origin}/post/${openPost}` });
          // Clean up URL
          window.history.replaceState(null, '', `/post/${openPost}`);
        } else if (openProfile) {
          console.log('🌐 Web redirect to profile:', openProfile);
          handleDeepLink({ url: `${window.location.origin}/profile/${openProfile}` });
          // Clean up URL
          window.history.replaceState(null, '', `/profile/${openProfile}`);
        } else if (openClub) {
          console.log('🌐 Web redirect to club:', openClub);
          handleDeepLink({ url: `${window.location.origin}/club/${openClub}` });
          // Clean up URL
          window.history.replaceState(null, '', `/club/${openClub}`);
        } else if (openContract) {
          console.log('🌐 Web redirect to contract:', openContract);
          handleDeepLink({ url: `${window.location.origin}/contract/${openContract}` });
          // Clean up URL
          window.history.replaceState(null, '', `/contract/${openContract}`);
        } else if (openSupply) {
          console.log('🌐 Web redirect to supply:', openSupply);
          handleDeepLink({ url: `${window.location.origin}/supply/${openSupply}` });
          // Clean up URL
          window.history.replaceState(null, '', `/supply/${openSupply}`);
        } else if (openProduct) {
          console.log('🌐 Web redirect to product:', openProduct);
          handleDeepLink({ url: `${window.location.origin}/product/${openProduct}` });
          // Clean up URL
          window.history.replaceState(null, '', `/product/${openProduct}`);
        } else {
          // Check pathname for deep links
          const pathname = window.location.pathname;
          if (pathname && pathname !== '/') {
            const fullUrl = window.location.origin + pathname;
            console.log('🌐 Web deep link from pathname:', fullUrl);
            handleDeepLink({ url: fullUrl });
          }
        }
      }
    });

    // Listen for deep link events while app is running
    const subscription = Linking.addEventListener('url', handleDeepLink);

    return () => {
      subscription.remove();
    };
  }, []);

  // Set status bar and navigation bar styling
  useEffect(() => {
    if (Platform.OS === 'android') {
      // Set status bar style
      StatusBar.setBarStyle(isDark ? 'light-content' : 'dark-content');
      StatusBar.setBackgroundColor(theme.background);

      // Set navigation bar color and button style
      NavigationBar.setBackgroundColorAsync(theme.background);
      NavigationBar.setButtonStyleAsync(isDark ? 'light' : 'dark');
    }
  }, [isDark, theme.background]);

  // Handle browser back button on web to prevent closing the tab
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();

      // Check if we can go back in the navigation stack
      if (navigationRef.current?.canGoBack()) {
        navigationRef.current?.goBack();
      } else {
        // If we're at the root, stay on the app instead of closing
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Push an initial state to prevent immediate back
    window.history.pushState(null, '', window.location.href);

    // Listen to popstate events (browser back button)
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <>
      <StatusBar
        barStyle={isDark ? 'light-content' : 'dark-content'}
        backgroundColor={theme.background}
      />
      <View style={[styles.navigationContainer, { backgroundColor: theme.background }]}>
        <NavigationContainer
          ref={navigationRef}
          documentTitle={{
            formatter: (options, route) => {
              return 'Leads n Business';
            }
          }}
        >
          <RootNavigator />
          <TutorialOverlay />
        </NavigationContainer>
      </View>
    </>
  );
};

export default function App() {
  const [fontsLoaded] = useFonts({
    Poppins_300Light,
    Poppins_400Regular,
    Poppins_500Medium,
    Poppins_600SemiBold,
    Poppins_700Bold,
  });
  // Disable animated splash on web since animations don't work properly
  const [showAnimatedSplash, setShowAnimatedSplash] = useState(Platform.OS !== 'web');

  // Apply Poppins globally once fonts are loaded
  useEffect(() => {
    if (fontsLoaded) {
      console.log('✅ Fonts loaded! Applying Poppins globally...');

      const customFontStyle = {
        style: {
          fontFamily: 'Poppins_400Regular',
        },
      };

      applyGlobalTextStyles(customFontStyle);

      console.log('✅ Poppins applied to all Text components');
    }
  }, [fontsLoaded]);

  // Initialize mock data once on app startup
  useEffect(() => {
    initializeMockLedgerData();
  }, []);

  // Skip font loading splash on web - just show app immediately
  if (!fontsLoaded && Platform.OS !== 'web') {
    return <BrandSplash />;
  }

  return (
    <>
      <SafeAreaProvider style={styles.rootContainer}>
        <ThemeProvider>
          <AuthProvider>
            <WorkProvider>
              <ChatProvider>
                <NotificationProvider>
                  <TutorialProvider>
                    <AppContent />
                  </TutorialProvider>
                </NotificationProvider>
              </ChatProvider>
            </WorkProvider>
          </AuthProvider>
        </ThemeProvider>
      </SafeAreaProvider>
      {showAnimatedSplash && (
        <BrandSplash animateOut onFinish={() => setShowAnimatedSplash(false)} />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  rootContainer: {
    flex: 1,
    backgroundColor: '#000000', // Match splash screen background
  },
  navigationContainer: {
    flex: 1,
  },
});
